import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import TextButton, { type TextButtonProps } from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowRight';
import ResaleIcon from '@wix/da-ds/pkg/Icons/16x16/Resale';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { pushExclusiveTransactionHistoryModal } from '../../../../../../Modals/redux/actionCreators';
import { useBatchedCallback } from '@wix/da-hooks/pkg/useBatchedCallback';
import s from './TradeCount.scss';

interface Props extends Omit<TextButtonProps, 'variant' | 'size' | 'children'> {
  deviation: PapiDeviation;
  withFlames?: boolean;
  count: number;
}

const TradeCount: React.FC<Props> = ({ deviation, count, withFlames, onClick, ...rest }) => {
  const { t } = useTranslation();
  const flameCount = Math.min(3, count);

  const dispatchPushExclusiveTransactionHistoryModal = useAction(
    pushExclusiveTransactionHistoryModal
  );

  const openTransactionHistoryModal = useCallback(() => {
    dispatchPushExclusiveTransactionHistoryModal(deviation);
  }, [deviation, dispatchPushExclusiveTransactionHistoryModal]);

  const handleClick = useBatchedCallback(openTransactionHistoryModal, onClick);

  if (withFlames) {
    return (
      <div className={s['root']}>
        <div className={s['flames']}>
          {Array(flameCount)
            .fill(0)
            .map((_, index) => (
              <img
                key={`flame-${index}`}
                alt=""
                className={s['flame']}
                src="https://st.deviantart.net/eclipse/icons/i9.14_tradingflame.gif"
              />
            ))}
        </div>
        <TextButton
          variant="main"
          size="small"
          postfix={ArrowRightIcon}
          onClick={handleClick}
          {...rest}
        >
          {t('sidebar.exclusives.tradeCount', { count })}
        </TextButton>
      </div>
    );
  }

  return (
    <TextButton
      variant="main"
      size="small"
      icon={ResaleIcon}
      postfix={ArrowRightIcon}
      onClick={handleClick}
      {...rest}
    >
      {t('sidebar.exclusives.tradeCount', { count })}
    </TextButton>
  );
};

TradeCount.displayName = 'TradeCount';

export default TradeCount;
